import { CongestionSettingSchema } from "./congestion";
import { DictStatusSchema } from "./bizDicts";
import { z } from "zod";

export enum FacilityOptionTypes {
  text = "text",
  phone = "phone_number",
  url = "url",
  map = "map",
  image = "image",
}

// hex
export enum FacilityOptionsColors {
  black = "000000",
  grey = "808080",
  blue = "0000FF",
  // green = "008000",
  // yellow = "FFFF00",
  orange = "FFA500",
  red = "FF0000",
}

export type QueryQcBizFacilityBizHour = z.infer<typeof QueryQcBizFacilityBizHourSchema>;
export const QueryQcBizFacilityBizHourSchema = z.object({
  description: z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? ""),
  description_dict: z.string().optional(),
  facility_id: z.number(),
  id: z.number(),
  order_no: z.number(),
  title: z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? ""),
  title_dict: z.string().optional(),
  description_dict_status: DictStatusSchema.optional(),
  title_dict_status: DictStatusSchema.optional(),
});

export type QueryQcBizFacilityImage = z.infer<typeof QueryQcBizFacilityImageSchema>;
export const QueryQcBizFacilityImageSchema = z.object({
  facility_id: z.number(),
  id: z.number(),
  order_no: z.number(),
  url: z.string(),
});

export type QueryQcBizFacilityOptions = z.infer<typeof QueryQcBizFacilityOptionsSchema>;
export const QueryQcBizFacilityOptionsSchema = z.object({
  id: z.number(),
  facility_id: z.number(),
  type: z.string(),
  label: z.string().nullable(),
  label_dict: z.string(),
  label_dict_status: DictStatusSchema,
  content: z.string(),
  is_bold: z.boolean(),
  is_underline: z.boolean(),
  color_code: z.string(),
  order_no: z.number(),
});

export type FacilityImage = z.infer<typeof FacilityImageSchema>;
export const FacilityImageSchema = z.object({
  id: z.number(),
  url: z.string(),
  isAdded: z.boolean().optional(),
  displaySrc: z.string().optional(),
});

export type UpdateQcBizFacilityBizHour = z.infer<typeof UpdateQcBizFacilityBizHourSchema>;
export const UpdateQcBizFacilityBizHourSchema = z.object({
  description: z.string().optional(),
  description_dict: z.string().optional(),
  facility_id: z.number().optional(),
  id: z.number().optional(),
  order_no: z.number().optional(),
  title: z.string().optional(),
  title_dict: z.string().optional(),
});

export type UpdateQcBizFacilityImage = z.infer<typeof UpdateQcBizFacilityImageSchema>;
export const UpdateQcBizFacilityImageSchema = z.object({
  facility_id: z.number().optional(),
  id: z.number().optional(),
  order_no: z.number().optional(),
  url: z.string().optional(),
});

export type FacilityOptions = z.infer<typeof FacilityOptionsSchema>;
export const FacilityOptionsSchema = z.object({
  id: z.number(),
  facilityId: z.number(),
  type: z.string(),
  label: z.string(),
  labelDict: z.string(),
  labelDictStatus: DictStatusSchema,
  content: z.string(),
  isBold: z.boolean(),
  isUnderline: z.boolean(),
  colorCode: z.string(),
  orderNo: z.number(),
  isAdded: z.boolean(),
});

export type FacilityBizHourAdmin = z.infer<typeof FacilityBizHourAdminSchema>;
export const FacilityBizHourAdminSchema = z.object({
  id: z.number(),
  description: z.string(),
  title: z.string(),
  isAdded: z.boolean().optional(),
  descriptionDictStatus: DictStatusSchema.optional(),
  descriptionDict: z.string().optional(),
  titleDictStatus: DictStatusSchema.optional(),
  titleDict: z.string().optional(),
});

export type QueryQcBizFacility = z.infer<typeof QueryQcBizFacilitySchema>;
export const QueryQcBizFacilitySchema = z.object({
  id: z.number(),
  order_no: z.number(),
  tenant_id: z.number(),
  sub_menu_id: z.number(),
  staff_group_id: z.number(),
  name: z.string(),
  name_dict: z.string(),
  name_dict_status: DictStatusSchema.optional(),
  description: z.string(),
  description_dict: z.string(),
  description_dict_status: DictStatusSchema.optional(),
  description2: z.string(),
  description2_dict: z.string(),
  description2_dict_status: DictStatusSchema.optional(),
  genre: z.string(),
  genre_dict: z.string(),
  genre_dict_status: DictStatusSchema.optional(),
  location: z.string(),
  location_dict: z.string(),
  location_dict_status: DictStatusSchema.optional(),
  business_hours: z.array(QueryQcBizFacilityBizHourSchema),
  images: z.array(QueryQcBizFacilityImageSchema),
  congestion_setting: CongestionSettingSchema,
  is_display: z.boolean(),
  facility_options: z.array(QueryQcBizFacilityOptionsSchema),
});

export type FacilityAdmin = z.infer<typeof FacilityAdminSchema>;
export const FacilityAdminSchema = z.object({
  id: z.number(),
  staff_group_id: z.number(),
  name: z.string(),
  description: z.string(),
  description2: z.string(),
  genre: z.string(),
  location: z.string(),
  business_hours: z.array(FacilityBizHourAdminSchema),
  images: z.array(FacilityImageSchema),
  nameDictStatus: DictStatusSchema.optional(),
  descriptionDictStatus: DictStatusSchema.optional(),
  description2DictStatus: DictStatusSchema.optional(),
  genreDictStatus: DictStatusSchema.optional(),
  locationDictStatus: DictStatusSchema.optional(),
  isRemoved: z.boolean(),
  isAdded: z.boolean().optional(),
  isEdited: z.boolean().optional(),
  isStaffGroupEdited: z.boolean().optional(),
  congestionSetting: CongestionSettingSchema,
  isDisplay: z.boolean(),
  facilityOptions: z.array(FacilityOptionsSchema),
});

export type UpdateQcBizFacilityWithDictStatus = z.infer<typeof UpdateQcBizFacilityWithDictSchema>;
export const UpdateQcBizFacilityWithDictSchema = z.object({
  id: z.number().optional().nullable(),
  order_no: z.number().optional().nullable(),
  tenant_id: z.number().optional().nullable(),
  sub_menu_id: z.number().optional().nullable(),
  staff_group_id: z.number().optional().nullable(),
  name: z.string().optional().nullable(),
  name_dict: z.string().optional().nullable(),
  name_dict_status: DictStatusSchema.optional().nullable(),
  description: z.string().optional().nullable(),
  description_dict: z.string().optional().nullable(),
  description_dict_status: DictStatusSchema.optional().nullable(),
  description2: z.string().optional().nullable(),
  description2_dict: z.string().optional().nullable(),
  description2_dict_status: DictStatusSchema.optional().nullable(),
  genre: z.string().optional().nullable(),
  genre_dict: z.string().optional().nullable(),
  genre_dict_status: DictStatusSchema.optional(),
  location: z.string().optional().nullable(),
  location_dict: z.string().optional().nullable(),
  business_hours: z.array(UpdateQcBizFacilityBizHourSchema).optional().nullable(),
  images: z.array(UpdateQcBizFacilityImageSchema).optional().nullable(),
  mode: z.number().optional().nullable(),
});
